import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkles, User, HelpCircle, Compass, CheckCircle } from "lucide-react";
import { announcements, getCurrentVersion } from "@/data/announcements";
import { useSiteSettings } from "@/hooks/useSiteSettings";

const STORAGE_KEY = "last_seen_announcement_version";

const iconMap: Record<string, any> = {
  user: User,
  "help-circle": HelpCircle,
  compass: Compass,
};

export const WhatsNewModal = () => {
  const [open, setOpen] = useState(false);
  const { data: settings } = useSiteSettings();

  useEffect(() => {
    const checkForNewAnnouncements = () => {
      // Check if What's New feature is enabled
      const whatsNewEnabled = settings?.find(s => s.key === 'whats_new_enabled')?.value === true;
      
      if (!whatsNewEnabled) {
        return;
      }

      const lastSeenVersion = localStorage.getItem(STORAGE_KEY);
      const currentVersion = getCurrentVersion();

      if (lastSeenVersion !== currentVersion) {
        setOpen(true);
      }
    };

    if (settings) {
      checkForNewAnnouncements();
    }
  }, [settings]);

  const handleClose = () => {
    localStorage.setItem(STORAGE_KEY, getCurrentVersion());
    setOpen(false);
  };

  const latestAnnouncement = announcements[0];

  if (!latestAnnouncement) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-6 w-6 text-primary" />
            <DialogTitle className="text-2xl">What's New</DialogTitle>
          </div>
          <DialogDescription>
            {latestAnnouncement.title} - Version {latestAnnouncement.version}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="text-sm text-muted-foreground">
            Released on {new Date(latestAnnouncement.date).toLocaleDateString('en-US', { 
              month: 'long', 
              day: 'numeric', 
              year: 'numeric' 
            })}
          </div>

          <div className="space-y-4">
            {latestAnnouncement.features.map((feature, index) => {
              const Icon = feature.icon ? iconMap[feature.icon] || CheckCircle : CheckCircle;
              
              return (
                <div key={index} className="flex gap-4 p-4 rounded-lg border bg-card">
                  <div className="flex-shrink-0">
                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Icon className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1">{feature.title}</h3>
                    <p className="text-sm text-muted-foreground">{feature.description}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {announcements.length > 1 && (
            <div className="pt-4 border-t">
              <h4 className="text-sm font-medium mb-3">Previous Updates</h4>
              <div className="space-y-2">
                {announcements.slice(1, 4).map((announcement, index) => (
                  <div key={index} className="text-sm flex justify-between items-center py-2">
                    <span className="text-muted-foreground">{announcement.title}</span>
                    <span className="text-xs text-muted-foreground">v{announcement.version}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button onClick={handleClose} className="w-full sm:w-auto">
            Got it, thanks!
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
